/**
 * Rule-based keyword extraction and category inference. This is the fallback when no AI keyword generation is
 * configured, and the input the lexical scorer is tuned for: domain words, not names or places.
 */
import { CONCEPTS } from "./text/concepts.js";
import { conceptsFor, isConceptTerm, normalizeText, singularize, STOPWORDS, tokens } from "./text/normalize.js";
const FIRST_NAMES = new Set(`james john robert michael william david richard joseph thomas charles christopher daniel matthew anthony mark
   donald steven paul andrew joshua kenneth kevin brian george timothy ronald edward jason jeffrey ryan jacob gary
   nicholas eric jonathan stephen larry justin scott brandon benjamin samuel gregory frank alexander raymond patrick
   jack dennis jerry tyler aaron jose adam nathan henry douglas zachary peter kyle ethan walter noah jeremy
   christian keith roger terry austin sean gerald carl harold dylan arthur lawrence jordan jesse bryan billy bruce
   gabriel joe logan alan juan albert willie elijah wayne randy vincent mason roy ralph bobby russell bradley
   philip eugene mary patricia jennifer linda elizabeth barbara susan jessica sarah karen lisa nancy betty sandra
   margaret ashley kimberly emily donna michelle carol amanda melissa deborah stephanie dorothy rebecca sharon
   laura cynthia amy kathleen angela shirley brenda emma anna pamela nicole samantha katherine christine helen
   debra rachel carolyn janet maria catherine heather diane olivia julie joyce victoria ruth virginia lauren kelly
   christina joan evelyn judith andrea hannah megan cheryl jacqueline martha madison teresa gloria sara janice ann
   kathryn abigail sophia frances jean alice judy isabella julia grace amber denise danielle marilyn beverly
   charlotte natalie theresa diana brittany doris kayla alexis lori marie ben sam tom mike bill bob jim dan dave
   chris matt tony steve joey jen liz kate meg beth sue annie eddie helen grace emily sarah diana ethan`
    .split(/\s+/)
    .filter(Boolean));
const PLACE_WORDS = new Set(`city county state street avenue road downtown uptown suburb village town neighborhood north south east west
   usa america canada uk london paris berlin tokyo sydney toronto vancouver chicago boston austin denver seattle
   portland miami atlanta dallas houston phoenix angeles francisco diego york jersey texas california florida
   ohio georgia virginia carolina washington oregon nevada arizona colorado utah michigan illinois`
    .split(/\s+/)
    .filter(Boolean));
/** "in Austin, TX", "near Portland", "based in San Jose": remove the place so it never becomes a keyword. */
function stripLocations(text) {
    return text
        .replace(/\b(?:in|near|around|from|at|based in|located in|serving)\s+(?:the\s+)?([A-Z][a-zA-Z.]+(?:\s+[A-Z][a-zA-Z.]+){0,2})(?:,\s*[A-Z]{2}\b)?/g, " ")
        .replace(/\b[A-Z][a-z]+,\s*[A-Z]{2}\b/g, " ")
        .replace(/\b\d{5}(?:-\d{4})?\b/g, " ");
}
/** "Sarah Johnson needs" -> " needs": a known first name plus an optional capitalised surname is a person. */
function stripNames(text) {
    return text.replace(/\b([A-Z][a-z]+)(?:\s+[A-Z][a-z]+(?:-[A-Z][a-z]+)?)?(?:'s)?\b/g, (whole, first) => (FIRST_NAMES.has(first.toLowerCase()) ? " " : whole));
}
function clean(text) {
    return stripNames(stripLocations(text));
}
function isCandidate(token) {
    return token.length >= 3 && !FIRST_NAMES.has(token) && !PLACE_WORDS.has(token) && !STOPWORDS.has(token);
}
/**
 * Keywords ranked by where they appear (title first), concept relevance and frequency, plus title bigrams and
 * repeated description bigrams. Names and locations are excluded; result is lowercase, deduplicated, capped.
 */
export function extractKeywords(title, description, max = 15, opts = {}) {
    const titleTokens = tokens(clean(title ?? "")).filter(isCandidate);
    const descTokens = tokens(clean(description ?? "")).filter(isCandidate);
    if (titleTokens.length === 0 && descTokens.length === 0)
        return [];
    const weight = new Map();
    const bump = (t, by) => weight.set(t, (weight.get(t) ?? 0) + by);
    titleTokens.forEach((t) => bump(t, 3));
    descTokens.forEach((t) => bump(t, 1));
    for (const t of weight.keys())
        if (isConceptTerm(t))
            bump(t, 2);
    const bigramCounts = new Map();
    const addBigrams = (list, by) => {
        for (let i = 0; i < list.length - 1; i++) {
            const bg = `${list[i]} ${list[i + 1]}`;
            bigramCounts.set(bg, (bigramCounts.get(bg) ?? 0) + by);
        }
    };
    addBigrams(titleTokens, 2);
    addBigrams(descTokens, 1);
    // Title bigrams always qualify (they weigh 2); description bigrams must repeat.
    const bigrams = [...bigramCounts.entries()]
        .filter(([, count]) => count >= 2)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([bg]) => bg);
    const ranked = [...weight.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([t]) => t);
    const conceptNames = opts.includeConcepts === false ? [] : conceptsFor([...titleTokens, ...descTokens]).map((c) => c.replace(/_/g, " "));
    const out = [];
    const seen = new Set();
    const push = (kw) => {
        if (out.length >= max)
            return;
        const key = kw.split(" ").map(singularize).join(" ");
        if (seen.has(key))
            return;
        seen.add(key);
        out.push(kw);
    };
    bigrams.slice(0, Math.max(2, Math.floor(max / 3))).forEach(push);
    ranked.forEach(push);
    conceptNames.forEach(push);
    return out;
}
/** Filler that survives the scorer's stopword list but never describes a need or an offer. */
const SUGGEST_NOISE = new Set(`etc going go went him her his hers us new old really thing things stuff lot lots little big small much many
   every each around through during before after again still already maybe kind sort bit way one two three well
   soon asap currently today tomorrow week weeks month months year years right sure ok okay yes something anything
   everything nothing somebody anybody everybody nobody people person guy guys folks able ask asked asking tell told
   knows knew known needs wanted ideally preferably preferred prefer must should build make made create creating
   set put take give run use using bring start starting`
    .split(/\s+/)
    .filter(Boolean));
/** Lowercase words with accents and punctuation removed, keeping "&" inside a word so "R&D" stays one term. */
function surfaceTokens(text) {
    return text
        .normalize("NFKD")
        .replace(/\p{M}+/gu, "")
        .toLowerCase()
        .replace(/(?<![a-z0-9])&|&(?![a-z0-9])/g, " ")
        .replace(/[^a-z0-9&\s]+/g, " ")
        .split(/\s+/)
        .filter(Boolean);
}
function isFiller(raw) {
    const token = singularize(raw.replace(/&/g, ""));
    return raw.length < 2 || /^\d+$/.test(raw) || STOPWORDS.has(raw) || STOPWORDS.has(token) || SUGGEST_NOISE.has(raw) || SUGGEST_NOISE.has(token) || FIRST_NAMES.has(raw) || PLACE_WORDS.has(raw);
}
/** Runs of consecutive descriptive words, split where punctuation, filler, names or places break them. */
function phraseRuns(text) {
    const runs = [];
    for (const clause of clean(text).split(/[,;:.!?()\[\]|/\n]+/)) {
        let run = [];
        for (const raw of surfaceTokens(clause)) {
            if (isFiller(raw)) {
                if (run.length)
                    runs.push(run);
                run = [];
                continue;
            }
            run.push(raw);
        }
        if (run.length)
            runs.push(run);
    }
    return runs;
}
/** A run of up to three words is one phrase; longer runs become pairs so no suggestion is a whole sentence. */
function phrasesOf(run) {
    if (run.length <= 3)
        return [run];
    const out = [];
    for (let i = 0; i < run.length; i += 2)
        out.push(run.slice(i, i + 2));
    return out;
}
function conceptWord(raw) {
    const bare = raw.replace(/&/g, "");
    return isConceptTerm(bare) || isConceptTerm(singularize(bare));
}
/**
 * Keyword suggestions for the editor: the phrases a person actually wrote ("tax accountant", "r&d credits"),
 * then the domain words inside them, then the canonical concepts they imply. Unlike extractKeywords, which feeds the
 * scorer and favours single tokens, this keeps multi-word terms intact so the list reads like a person made it.
 * Description phrases qualify only when they contain a domain word; title phrases always do.
 */
export function suggestKeywords(title, description, max = 15) {
    const out = [];
    const seen = new Set();
    const push = (words) => {
        if (out.length >= max || words.length === 0)
            return;
        const key = words.map((w) => singularize(w)).join(" ");
        if (seen.has(key))
            return;
        seen.add(key);
        out.push(words.join(" "));
    };
    const titleRuns = phraseRuns(title ?? "");
    const descRuns = phraseRuns(description ?? "");
    const titlePhrases = titleRuns.flatMap(phrasesOf);
    const descPhrases = descRuns.flatMap(phrasesOf).filter((p) => p.some(conceptWord));
    titlePhrases.forEach(push);
    descPhrases.forEach(push);
    // The domain words inside multi-word phrases stand on their own too: "tax accountant" also matches "accountant".
    for (const phrase of [...titlePhrases, ...descPhrases]) {
        if (phrase.length < 2)
            continue;
        for (const word of phrase)
            if (conceptWord(word))
                push([word.includes("&") ? word : singularize(word)]);
    }
    const allWords = [...titleRuns, ...descRuns].flat().map((w) => singularize(w.replace(/&/g, "")));
    for (const concept of conceptsFor(allWords))
        push(concept.replace(/_/g, " ").split(" "));
    return out;
}
/** Category patterns: singular normalised tokens, plus a few phrases checked against the whole text. */
const CATEGORY_PATTERNS = [
    {
        name: "Home Services",
        terms: ["home", "house", "repair", "handyman", "contractor", "renovation", "remodel", "plumber", "electrician", "roof", "lawn", "cleaning", "hvac", "paint", "floor", "appliance", "landscaping", "mover", "moving"],
        concepts: ["electrical", "plumbing", "hvac", "roofing", "carpentry", "construction", "flooring", "painting", "landscaping", "cleaning", "moving", "appliance"],
    },
    { name: "Legal", terms: ["legal", "law", "attorney", "lawyer", "court", "contract", "litigation", "counsel", "paralegal", "notary", "estate", "immigration"], concepts: ["legal"] },
    {
        name: "Finance",
        terms: ["finance", "financial", "accounting", "accountant", "tax", "bookkeeping", "investment", "budget", "loan", "banking", "insurance", "mortgage", "cpa", "payroll", "wealth", "retirement"],
        concepts: ["accounting", "tax", "bookkeeping", "insurance", "mortgage", "financial_planning"],
    },
    {
        name: "Health",
        terms: ["health", "medical", "doctor", "nurse", "therapy", "therapist", "wellness", "patient", "clinic", "hospital", "dentist", "dental", "physician", "counseling", "nutrition", "dietitian", "fitness", "trainer"],
        concepts: ["medical", "dental", "therapy", "nutrition", "fitness"],
    },
    {
        name: "Technology",
        terms: ["software", "developer", "development", "programming", "code", "app", "website", "web", "frontend", "backend", "api", "database", "it", "helpdesk", "network", "computer", "hardware", "cybersecurity", "saas", "data"],
        concepts: ["software", "web_development", "it_support"],
    },
    {
        name: "Creative",
        terms: ["design", "designer", "graphic", "logo", "brand", "branding", "illustration", "photographer", "photography", "video", "videographer", "film", "writer", "writing", "editor", "editing", "copywriter", "music", "musician", "animation", "creative"],
        concepts: ["design", "photography", "video", "writing", "music"],
    },
    { name: "Education", terms: ["education", "teach", "teacher", "tutor", "tutoring", "lesson", "class", "course", "school", "instructor", "student", "learning", "curriculum", "training", "coach"], concepts: ["tutoring"] },
    { name: "Events", terms: ["event", "wedding", "catering", "caterer", "party", "venue", "planner", "dj", "florist", "entertainment", "conference", "banquet"], concepts: ["event_planning", "catering"] },
    {
        name: "Business Services",
        terms: ["consulting", "consultant", "strategy", "management", "marketing", "advertising", "seo", "recruiting", "recruiter", "staffing", "hr", "hiring", "fundraising", "nonprofit", "grant", "printing", "signage", "translation", "translator", "operations", "startup", "sales"],
        concepts: ["consulting", "marketing", "hr_recruiting", "fundraising", "printing", "translation"],
    },
    { name: "Automotive", terms: ["car", "vehicle", "auto", "automotive", "mechanic", "truck", "tire", "brake", "engine", "transmission", "detailing", "dealer", "towing"], concepts: ["automotive"] },
    { name: "Real Estate", terms: ["realtor", "realestate", "property", "apartment", "rent", "rental", "lease", "landlord", "tenant", "listing", "condo", "appraisal", "housing", "broker"], concepts: ["real_estate"] },
    { name: "Personal Care", terms: ["hair", "salon", "barber", "stylist", "massage", "spa", "nail", "nails", "makeup", "skincare", "esthetician", "lash", "beauty", "grooming"], concepts: [] },
    { name: "Pets", terms: ["pet", "dog", "cat", "puppy", "kitten", "vet", "veterinarian", "groomer", "boarding", "walker", "kennel"], concepts: ["pet_care"] },
    { name: "Travel", terms: ["travel", "trip", "vacation", "flight", "hotel", "tour", "tourism", "cruise", "itinerary", "airbnb", "resort", "visa"], concepts: [] },
    { name: "Childcare", terms: ["childcare", "babysitter", "nanny", "daycare", "sitter", "preschool", "toddler"], concepts: ["childcare"] },
    { name: "Security", terms: ["security", "guard", "alarm", "surveillance", "locksmith", "cctv"], concepts: ["security"] },
];
export const DEFAULT_CATEGORY = "General Services";
export const CATEGORY_NAMES = [...CATEGORY_PATTERNS.map((c) => c.name), DEFAULT_CATEGORY];
/**
 * Best matching category by weighted term hits: title-position is unknown here, so every token weighs the same,
 * a detected concept adds a strong bonus, and ties resolve in definition order (more specific lists first).
 */
export function inferCategory(text) {
    const list = tokens(text);
    if (list.length === 0)
        return DEFAULT_CATEGORY;
    const normalized = normalizeText(text);
    const present = new Set(list);
    const concepts = new Set(conceptsFor(list));
    let best = DEFAULT_CATEGORY;
    let bestScore = 0;
    for (const cat of CATEGORY_PATTERNS) {
        let score = 0;
        for (const term of cat.terms) {
            if (present.has(singularize(term)))
                score += 3;
            else if (term.includes(" ") && normalized.includes(term))
                score += 3;
        }
        for (const c of cat.concepts)
            if (concepts.has(c))
                score += 4;
        if (score > bestScore) {
            best = cat.name;
            bestScore = score;
        }
    }
    return best;
}
/** Keywords plus category in one call, the shape the server stores on a Need or Service. */
export function generateKeywordsAndCategory(title, description) {
    return { keywords: extractKeywords(title, description), category: inferCategory(`${title} ${description ?? ""}`) };
}
export const CONCEPT_NAMES = Object.keys(CONCEPTS);
//# sourceMappingURL=keywords.js.map